import { Field, Form, Formik } from 'formik';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { WorkspaceMemberRoleDisplayName, type WorkspaceUserRole } from '@air/api/types';
import { ChevronDownIcon } from '@air/design-icons';
import { Badge } from '@air/primitive-badge';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';

import { DropdownMenuWithSelectedLabelButton } from '~/components/Menus/DropdownMenuWithSelectedLabelButton';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { usePlanFeature } from '~/hooks/usePlanFeature';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { useWorkspaceMemberRoles } from '~/swr-hooks/members/useWorkspaceMemberRoles';
import { getLeastPermissiveRole, isPermissionsGTE } from '~/utils/PermissionsUtils';

type InviteByLinkInputProps = {
  onLinkGenerate: (role: WorkspaceUserRole) => void;
};

const GenerateLinkSchema = Yup.object()
  .shape({
    selectedRole: Yup.object<WorkspaceUserRole>().required('Please choose a role'),
  })
  .required();

export const InviteByLinkInput = ({ onLinkGenerate }: InviteByLinkInputProps) => {
  const { memberRoles } = useWorkspaceMemberRoles();
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const [showPlansModal] = useAirModal(PlansModal);
  const canHaveCommenters = usePlanFeature('commentOnlyMembers');
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const filteredRoles = useMemo(() => {
    if (!memberRoles) {
      return undefined;
    }

    return memberRoles.filter((role) => role.enabled !== false);
  }, [memberRoles]);

  const leastPermissiveRole = useMemo(() => {
    const roles = filteredRoles?.filter(
      (role) => canHaveCommenters || role.displayName !== WorkspaceMemberRoleDisplayName.Commenter,
    );

    return roles ? getLeastPermissiveRole(roles) : undefined;
  }, [canHaveCommenters, filteredRoles]);

  if (!filteredRoles || !currentWorkspaceMember || !workspacePermissions || !leastPermissiveRole) {
    return null;
  }

  return (
    <div className="relative mt-4 flex items-center">
      <Formik
        validationSchema={GenerateLinkSchema}
        initialValues={{
          selectedRole: leastPermissiveRole,
        }}
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onSubmit={({ selectedRole }) => onLinkGenerate(selectedRole)}
      >
        {({ setFieldValue, values: { selectedRole }, isSubmitting }) => (
          <div className="w-full">
            <Form>
              <div className="flex items-center">
                <Field as="children" name="selectedRole" id="selectedRole">
                  {() => (
                    <DropdownMenuWithSelectedLabelButton
                      trigger={
                        <div className="mr-3 flex h-12 flex-1 cursor-pointer items-center rounded-4 border ads:border-input border-pigeon-200 ads:bg-input bg-white px-3 ads:hover:bg-hover-subtle hover:bg-pigeon-25">
                          <div className="flex-1 text-left ads:text-secondary text-14 text-pigeon-600">
                            {selectedRole.displayName}
                          </div>
                          <div className="ml-1 flex items-center">
                            <ChevronDownIcon className="ads:text-icon-subtle text-pigeon-400" size="sm" />
                          </div>
                        </div>
                      }
                      selectedOption={{
                        id: selectedRole.id,
                        label: selectedRole.displayName,
                      }}
                      options={filteredRoles.map((role) => {
                        const { displayName, description, id } = role;

                        return !canHaveCommenters && displayName === WorkspaceMemberRoleDisplayName.Commenter
                          ? {
                              id,
                              onSelect: showPlansModal,
                              label: (
                                <span className="flex gap-1 overflow-visible whitespace-normal">
                                  {displayName}
                                  <Badge className="shrink-0" color="teal">
                                    Upgrade
                                  </Badge>
                                </span>
                              ),
                              description: description || undefined,
                              type: 'item',
                            }
                          : {
                              id,
                              onSelect: () => setFieldValue('selectedRole', role),
                              label: displayName,
                              description: description || undefined,
                              disabled: !isPermissionsGTE(workspacePermissions, role.permissions),
                              type: 'item',
                            };
                      })}
                    />
                  )}
                </Field>
                <Button
                  isLoading={isSubmitting}
                  appearance="filled"
                  color="blue"
                  type="submit"
                  size="extra-large"
                  disabled={isSubmitting}
                >
                  Create link
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </div>
  );
};
